import type React from 'react';
import type { RSInput, RSTextarea, RSCheckbox } from 'reactsymbols-kit';

/**
 * Library-boundary helpers for `reactsymbols-kit` form components, shared by the
 * inspector panels. All are type-level only and return their argument unchanged
 * at runtime, so the migrated JSX stays byte-identical.
 *
 * Two genuine kit-typing gaps motivate this module:
 *
 * 1. `RSInput`/`RSTextarea`/`RSCheckbox`/`RSRadio` extend `React.HTMLProps`, so
 *    their declared `onChange` is the unusable INTERSECTION of the DOM
 *    `FormEventHandler` with the kit's own value callback — no single handler
 *    satisfies both. `asRSChange` re-types the real value handler as whatever
 *    `onChange` shape the target prop declares. This is the same escape the
 *    committed `ReportView` / `ModelSettingsForm` use; centralised here because
 *    every inspector needs it.
 *
 * 2. The kit declares `value` / `level` / `text` (and `selectedValue`) as plain
 *    `string` (or, for `value`, the DOM `string | number | …` that excludes
 *    `null`). The inspectors legitimately pass the pending-change value, which is
 *    `string | null` at runtime (and the kit renders `null` as an absent prop,
 *    exactly as the original JS relied on). `rsValue` re-types such a value to
 *    the prop's declared shape without touching the runtime value.
 */

/** The (broken-intersection) `onChange` prop type of `RSInput`. */
export type RSInputChange = React.ComponentProps<typeof RSInput>['onChange'];
/** The (broken-intersection) `onChange` prop type of `RSTextarea`. */
export type RSTextareaChange = React.ComponentProps<typeof RSTextarea>['onChange'];
/** The (broken-intersection) `onChange` prop type of `RSCheckbox`. */
export type RSCheckboxChange = React.ComponentProps<typeof RSCheckbox>['onChange'];

/**
 * Re-type a real value-based change handler as the `onChange` prop type the
 * target kit component declares (see gap 1). Runtime handler is unchanged.
 */
export function asRSChange<T>(handler: (...args: never[]) => void): T {
  return handler as unknown as T;
}

/**
 * Re-type a `string | null` (pending-change) value as the `string` the kit's
 * `value` / `level` / `text` props declare (see gap 2). The runtime value —
 * including `null` — is passed through untouched.
 */
export function rsValue(value: string | null): string {
  return value as string;
}
